import { FigureReorderDialog } from "components/system";
import { useModal } from "mui-modal-provider";
import { useEffect, useState } from "react";
import { useHistory, useParams } from "react-router";
import { useAppContext } from "../../../context/AuthContext";
import { ArticleController, ArticleControllerType } from "../../../controller/article/ArticleController";
import LabelsContext from "../../../lang/LabelsContext";
import MessageType from "../../../utils/MessageType";
import { getTypeFromNameFile, handleErrorMessage, useEffectOnce } from "../../../utils/Utils";
import EnumDesignation from "../../../utils/enum/EnumDesignation";

interface CorrectionServiceType {
  uploadFile: any;
  filesList: any;
  filesByAssistant: any;
  setFilesList: any;
  designationOptions: any;
  openDialog: any;
  handleOpenDialog: (id?: number) => void;
  handleCloseDialog: any;
  handleResetDialog: any;
  files: any;
  setFiles: any;
  uploading: boolean;
  submit: () => void;
  correctionText: string;
  selectedDesignation: any;
  articleRevision?: number;
}

export default function CorrectionService(): CorrectionServiceType {
  const {
    axiosGetArticleById,
    axiosUploadFile,
    axiosPostDeleteFile,
    axiosPostSubmitCorrection,
  }: ArticleControllerType = ArticleController();
  const { showModal } = useModal();
  const { showMessage, setBlockUI } = useAppContext();
  const { Labels } = LabelsContext();
  const [filesList, setFilesList] = useState<any>([]);
  const [uploading, setUploading] = useState<boolean>(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [correctionText, setCorrectionText] = useState<any>();
  const [article, setArticle] = useState<any>();
  const [articleRevision, setArticleRevision] = useState<number | undefined>();
  const [selectedFile, setSelectedFile] = useState<any>();

  const [selectedDesignation, setSelectedDesignation] = useState<any>();

  const [filesByAssistant, setFilesByAssistant] = useState<any>({});

  const [files, setFiles] = useState<File[]>([]);
  const { id }: any = useParams();
  const history = useHistory();

  useEffectOnce(() => {
    getArticle();
  });

  const getArticle = () =>
    axiosGetArticleById(id)
      .then((res: any) => {
        const data = res.data;
        setArticle(data);
        setArticleRevision(data.revision);
        const files = data?.files?.map((f: any, i: number) => ({
          id: i + 1,
          name: f.filename,
          designation: f.type,
          type: getTypeFromNameFile(f.filename),
          location: f.location,
          revision: Number(f.revision),
          figureTitle: f.figureTitle,
          new: false,
        }));
        setFilesList(files);
        setCorrectionText(data.articleCorrections.length > 0 ? data.articleCorrections[0].description : undefined);
      })
      .catch((error: any) => handleErrorMessage(showMessage, error));

  useEffect(() => {
    const filterByRevision: any = {};
    if (articleRevision || articleRevision === 0) {
      [...Array(articleRevision + 1)].reverse().forEach((n: any, index: number) => {
        filterByRevision[index] = filesList.filter((x: any) => x.revision === index);
      });
    }
    setFilesByAssistant(filterByRevision);
  }, [filesList, articleRevision]);

  const uploadFile = (file: any, values: any) => {
    setUploading(true);
    setBlockUI(true);
    axiosUploadFile(id, file)
      .then((response: any) => {
        if (selectedFile) {
          axiosPostDeleteFile(article.id, selectedFile);
        }

        const fileNew = {
          id: filesList.length + 1,
          name: response.data.fileName,
          designation: values.designation.code,
          type: values.type,
          location: URL.createObjectURL(file[0]),
          revision: articleRevision,
          new: true,
        };
        let newFiles = filesList;
        if (selectedDesignation) {
          newFiles = newFiles.filter((f: any) => f.id !== selectedDesignation.id);
        }

        setFilesList([fileNew, ...newFiles]);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      })
      .finally(() => {
        setBlockUI(false);
        setUploading(false);
      });
  };

  const reorderFiles = (files: any) => {
    const isExistFigures = files.some((f: any) =>
      [EnumDesignation.FIGURE, EnumDesignation.SUPPLEMENTARY_FIGURE].includes(f.type)
    );
    if (isExistFigures) {
      showModal(FigureReorderDialog, {
        article: {
          ...article,
          files,
        },
        onConfirm: submitRevision,
      });
    } else {
      submitRevision({ files });
    }
  };

  const submitRevision = ({ files }: any) => {
    setBlockUI(true);

    const newFiles = files?.map((f: any) => {
      const newF = f;
      delete newF.id;
      return newF;
    });

    axiosPostSubmitCorrection(id, {
      articleId: id,
      files: newFiles.length > 0 ? newFiles : [],
    })
      .then((response: any) => {
        history.push("/manuscript/view");
        showMessage("Files successfully uploaded.", MessageType.SUCCESS);
      })
      .catch((error: any) => {
        handleErrorMessage(showMessage, error);
      })
      .finally(() => setBlockUI(false));
  };

  const submit = () => {
    const files = filesList
      .filter((f: any) => f.revision === articleRevision)
      .map((f: any) => ({
        id: f.id,
        filename: f.name,
        type: f.designation,
        fileType: f.type,
        figureTitle: f.figureTitle,
      }));
    reorderFiles(files);
  };

  const designationOptions0Revision: any = [
    { name: Labels.MANUSCRIPT, code: EnumDesignation.MANUSCRIPT },
    { name: Labels.FIGURE, code: EnumDesignation.FIGURE },
    { name: Labels.ADDITIONAL_FILE, code: EnumDesignation.ADDITIONAL_FILE },
    { name: Labels.VIDEO_FILE, code: EnumDesignation.VIDEO_FILE },
    { name: Labels.ABSTRACT, code: EnumDesignation.ABSTRACT },
    { name: "Cover letter", code: EnumDesignation.COVER_LETTER },
    { name: "Tables (combine tables into one document)", code: EnumDesignation.TABLES },
  ];

  const designationOptions: any = [
    ...designationOptions0Revision,
    { name: "Unmarked Manuscript", code: EnumDesignation.UNMARKED_MANUSCRIPT },
    { name: "Marked manuscript", code: EnumDesignation.MARKED_MANUSCRIPTS },
    { name: "Letter of Rebuttal", code: EnumDesignation.LETTER_OF_REBUTAL },
  ];

  const designationCurrent = articleRevision === 0 ? designationOptions0Revision : designationOptions;

  const handleOpenDialog = (id?: number) => {
    if (id) {
      setSelectedDesignation(filesList.find((d: any) => d.id === id));
    } else {
      setSelectedDesignation(null);
    }

    setSelectedFile(id && !isNaN(id) ? id : null);

    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleResetDialog = (disabled: boolean, setFieldValue: any, files: File[], size: any, type: any) => {
    if (files !== null) setFiles(files);
    setFieldValue("size", size);
    setFieldValue("type", type);
  };

  return {
    uploadFile,
    filesList,
    setFilesList,
    designationOptions: designationCurrent,
    openDialog,
    handleOpenDialog,
    handleCloseDialog,
    handleResetDialog,
    files,
    setFiles,
    uploading,
    submit,
    correctionText,
    selectedDesignation,
    filesByAssistant,
    articleRevision,
  };
}

export type { CorrectionServiceType };
